import { HiSpeakerWave, HiSpeakerXMark } from 'react-icons/hi2';

interface VolumeControlProps {
  volume: number;
  muted: boolean;
  onVolumeChange: (volume: number) => void;
  onMuteToggle: () => void;
}

export function VolumeControl({
  volume,
  muted,
  onVolumeChange,
  onMuteToggle,
}: VolumeControlProps) {
  const displayVolume = muted ? 0 : volume;

  return (
    <div className="flex items-center gap-2 group">
      <button
        onClick={onMuteToggle}
        aria-label={muted ? 'Unmute' : 'Mute'}
        className="p-1.5 text-[var(--color-text-secondary)]
                   hover:text-[var(--color-text)]
                   transition-colors duration-150
                   focus:outline-none focus-visible:ring-2
                   focus-visible:ring-[var(--color-primary)] rounded-md"
      >
        {muted || volume === 0 ? (
          <HiSpeakerXMark size={18} />
        ) : (
          <HiSpeakerWave size={18} />
        )}
      </button>

      {/* Compact volume slider */}
      <div className="relative w-16 h-6 flex items-center">
        {/* Background track */}
        <div className="absolute inset-x-0 h-1 bg-[var(--color-border)] rounded-full" />

        {/* Volume fill */}
        <div
          className="absolute left-0 h-1 bg-[var(--color-text-muted)]
                     group-hover:bg-[var(--color-primary)]
                     rounded-full transition-colors duration-150"
          style={{ width: `${displayVolume * 100}%` }}
        />

        <input
          type="range"
          min="0"
          max="1"
          step="0.05"
          value={displayVolume}
          onChange={(e) => onVolumeChange(parseFloat(e.target.value))}
          aria-label="Volume"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(displayVolume * 100)}
          aria-valuetext={`${Math.round(displayVolume * 100)} percent`}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        />

        {/* Custom thumb */}
        <div
          className="absolute w-2.5 h-2.5 bg-[var(--color-text-muted)]
                     group-hover:bg-[var(--color-primary)]
                     rounded-full transform -translate-x-1/2
                     pointer-events-none transition-colors duration-150"
          style={{ left: `${displayVolume * 100}%` }}
        />
      </div>
    </div>
  );
}
